import type { ReactNode } from "react";
import styled from "styled-components";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  H3,
  Icon,
  Modal,
  P,
  Small,
} from "orcs-design-system";

import GuardrailHistory from "src/inbox/requisition/GuardrailHistory";
import { guardrailHistory } from "src/inbox/requisition/guardrailHistoryRows";
import {
  formatClockHours,
  formatRelativeAgo,
  liveElapsedHours,
  showPastSla,
} from "src/pages/WorkforceGuardrails/clockHours";
import type { RefusalReason } from "src/pages/WorkforceGuardrails/ruleShape";

import ExceptionReasonForm, {
  EXCEPTION_REASON_MODAL_TITLE,
} from "./ExceptionReasonForm";
import { ExceptionReasonAttribution } from "./exceptionReasonAttribution";
import { PersonNameLink } from "./PersonNameLink";

import GuardrailCheckList, { GuardrailCardHeader } from "./GuardrailCheckList";
import GuardrailDecisionForm from "./GuardrailDecisionForm";
import {
  useGuardrailChainActions,
  type ChainActions,
} from "./useGuardrailChainActions";

export { withCheckLabels } from "./guardrailRuleLabels";

export type GuardrailCategory = "COST" | "LOCATION" | "STRUCTURE" | "STRATEGIC";

export type GuardrailCheck = {
  ruleId: string;
  /** A WARN failure stays on the stamp. It does not change the decision. */
  effect?:
    | "BLOCK"
    | "WARN"
    | "ADD_APPROVAL_STEP"
    | "SKIP_APPROVAL_STEP"
    | "AUTO_APPROVE"
    | null;
  state: "PASS" | "FAIL" | "UNKNOWN";
  detail: string;
  /** Rule name from the monitor. Used when category is missing. */
  name?: string | null;
  description?: string | null;
  category?: GuardrailCategory | null;
  ownerRole?: string | null;
};

export type GuardrailDecision = "PASS" | "HARD_BLOCK";

export type ChainTemplate = "standard" | "exception" | "fast-track";

export type AddingRule = {
  ruleId: string;
  ruleName?: string | null;
};

export type ApprovalGroup = {
  approverGroupId: string;
  approverGroupName?: string | null;
  status?: string | null;
  personId?: string | null;
  personName?: string | null;
  actedAt?: string | null;
  addingRules?: AddingRule[] | null;
  /** Closed clock hours. Null when the clock has not started. */
  elapsedHours?: number | null;
  clockStartedAt?: string | null;
  /** Null when the group has no SLA. */
  slaHours?: number | null;
  pastSla?: boolean | null;
  canRespond?: boolean | null;
  canUndo?: boolean | null;
  /** Labels stored at decision time. Do not look these up again. */
  reasons?: Array<{ id: string; label: string }> | null;
  comment?: string | null;
  /** Dynamic group. Absent on an ordinary group. */
  resolver?: string | null;
  /** Person resolved when the requisition was raised. Absent when none. */
  assigneePersonId?: string | null;
  assigneePersonName?: string | null;
};

export type ApprovalStep = {
  order: number;
  kind: string;
  ruleId?: string | null;
  /** Reason step only. A group step keeps status on each group. */
  status?: string | null;
  reason?: string | null;
  personId?: string | null;
  personName?: string | null;
  actedAt?: string | null;
  /** Approver groups on a group step, in order. */
  groups?: ApprovalGroup[] | null;
};

export type GuardrailEvaluation = {
  decision: GuardrailDecision;
  checks: GuardrailCheck[];
  evaluatedAt?: string | null;
  trigger?: string | null;
  template?: ChainTemplate | string | null;
  steps?: ApprovalStep[] | null;
};

export type GuardrailDecisionLogRescind = {
  personId?: string | null;
  personName?: string | null;
  actedAt?: string | null;
};

export type GuardrailDecisionLogEntry = {
  decision: "APPROVED" | "REFUSED";
  approverGroupId: string;
  approverGroupName?: string | null;
  order: number;
  personId?: string | null;
  personName?: string | null;
  actedAt?: string | null;
  reasons?: Array<{ id: string; label: string }> | null;
  comment?: string | null;
  rescindedBy?: GuardrailDecisionLogRescind | null;
};

export type GuardrailResult = {
  id?: string | null;
  openedDate?: string | null;
  guardrailDecision?: GuardrailDecision | null;
  guardrailEvaluation?: GuardrailEvaluation | null;
  guardrailDecisionLog?: GuardrailDecisionLogEntry[] | null;
  lifecycleEvents?: Array<{
    from?: string | null;
    to?: string | null;
    at?: string | null;
  }> | null;
};

const isGuardrailDecision = (value: unknown): value is GuardrailDecision =>
  value === "PASS" || value === "HARD_BLOCK";

export type HeaderBadge = {
  label: string;
  variant: "success" | "warning" | "danger" | "primary";
};

const passBadge = (template: GuardrailEvaluation["template"]): HeaderBadge => {
  switch (template) {
    case "exception":
      return { label: "Exception", variant: "warning" };
    case "fast-track":
      return { label: "Fast-track", variant: "primary" };
    case "standard":
    case null:
    case undefined:
      return { label: "Pass", variant: "success" };
    default:
      return { label: "Pass", variant: "success" };
  }
};

const headerBadge = (
  decision: GuardrailDecision,
  template: GuardrailEvaluation["template"]
): HeaderBadge => {
  switch (decision) {
    case "HARD_BLOCK":
      return { label: "Hard block", variant: "danger" };
    case "PASS":
      return passBadge(template);
    default: {
      const unexpected: never = decision;
      return unexpected;
    }
  }
};

/** Registry tables and summaries — same labels as the outcome header badge. */
export const getGuardrailDecisionDisplay = (
  decision: GuardrailDecision | null | undefined,
  template?: GuardrailEvaluation["template"] | null
): HeaderBadge | null => {
  if (!isGuardrailDecision(decision)) {
    return null;
  }
  return headerBadge(decision, template ?? null);
};

const groupStepNumber = (steps: ApprovalStep[], order: number): number => {
  let count = 0;
  for (const step of sortedSteps(steps)) {
    if (step.kind !== "group") continue;
    count += 1;
    if (step.order === order) return count;
  }
  return count;
};

const stepTitle = (step: ApprovalStep, steps: ApprovalStep[]): string => {
  switch (step.kind) {
    case "evaluation":
      return "Automatic evaluation";
    case "reason":
      return "Exception reason";
    case "group":
      return `Step ${groupStepNumber(steps, step.order)}`;
    default:
      return step.kind;
  }
};

const statusLabel = (status: string | null | undefined): string => {
  switch (status) {
    case "PENDING":
      return "Pending";
    case "APPROVED":
      return "Approved";
    case "REFUSED":
      return "Refused";
    default:
      return "";
  }
};

const EXCEPTION_REASON_CTA = "Add exception reason";

const sortedSteps = (steps: ApprovalStep[] | null | undefined) =>
  [...(steps ?? [])].sort((left, right) => left.order - right.order);

const groupsOrEmpty = (groups: ApprovalGroup[] | null | undefined) =>
  groups ?? [];

const stepGroupsOf = (step: ApprovalStep): ApprovalGroup[] => {
  if (step.kind === "group") return groupsOrEmpty(step.groups);
  return [];
};

const stepWidth = (count: number) => {
  if (count > 1) return "320px";
  return "260px";
};

const groupIsPast = (nowMs: number) => (group: ApprovalGroup) =>
  showPastSla({
    slaHours: group.slaHours,
    pastSla: group.pastSla === true,
    elapsedHours: group.elapsedHours,
    clockStartedAt: group.clockStartedAt,
    nowMs,
  });

const slaTextOf = (slaHours: number | null | undefined) => {
  if (typeof slaHours === "number") return `SLA ${formatClockHours(slaHours)}`;
  return null;
};

const storedDecisionParts = (group: ApprovalGroup): string[] => {
  const labels = (group.reasons ?? []).flatMap((reason) =>
    reason.label.trim() === "" ? [] : [reason.label]
  );
  const comment = group.comment ?? "";
  if (comment.trim() === "") return labels;
  return [...labels, comment];
};

const withStoredDecision = (line: string, group: ApprovalGroup) => {
  const parts = storedDecisionParts(group);
  if (parts.length === 0) return line;
  return `${line}. ${parts.join(". ")}`;
};

const withStoredDecisionNode = (
  line: ReactNode,
  group: ApprovalGroup
): ReactNode => {
  const parts = storedDecisionParts(group);
  if (parts.length === 0) return line;
  return (
    <>
      {line}. {parts.join(". ")}
    </>
  );
};

const groupStatusLine = (group: ApprovalGroup, nowMs: number): ReactNode => {
  const name = groupNameOf(group);
  const status = statusLabel(group.status);
  if (status === "Approved" || status === "Refused") {
    const who = group.personName?.trim() || "Unknown";
    const rel =
      typeof group.actedAt === "string"
        ? formatRelativeAgo(group.actedAt, nowMs)
        : null;
    const whoNode = <PersonNameLink name={who} personId={group.personId} />;
    const line = rel ? (
      <>
        {name}: {status} by {whoNode} {rel}
      </>
    ) : (
      <>
        {name}: {status} by {whoNode}
      </>
    );
    return withStoredDecisionNode(line, group);
  }
  if (status) {
    const line = withStoredDecision(`${name}: ${status}`, group);
    if (group.resolver && !group.assigneePersonId?.trim()) {
      return `${line}. No person.`;
    }
    if (status === "Pending" && group.assigneePersonId) {
      const who = group.assigneePersonName?.trim() || group.assigneePersonId;
      return (
        <>
          {line}.{" "}
          <PersonNameLink name={who} personId={group.assigneePersonId} />
        </>
      );
    }
    return line;
  }
  return withStoredDecision(name, group);
};

const undoLabel = (group: ApprovalGroup) => {
  if (group.status === "REFUSED") return "Rescind Refusal";
  return "Rescind Approval";
};

const trimmedName = (group: ApprovalGroup) => group.approverGroupName?.trim();

const groupNameOf = (group: ApprovalGroup) =>
  trimmedName(group) || "Approver group";

const stepLabel = (step: ApprovalStep) => {
  if (step.kind === "group") return "";
  return statusLabel(step.status);
};

const withStatus = (title: string, label: string) => {
  if (label) return `${title}: ${label}`;
  return title;
};

const stepHeading = (step: ApprovalStep, steps: ApprovalStep[]) =>
  withStatus(stepTitle(step, steps), stepLabel(step));

const stepBackground = (past: boolean) => {
  if (past) return "dangerLightest" as const;
  return undefined;
};

const stepPast = (groups: ApprovalGroup[], nowMs: number) =>
  groups.some(groupIsPast(nowMs));

const reasonValue = (step: ApprovalStep) => step.reason ?? "";

const isReasonStep = (step: ApprovalStep) => step.kind === "reason";

const isPending = (step: ApprovalStep) => step.status === "PENDING";

const elapsedText = (group: ApprovalGroup, nowMs: number) => {
  const hours = liveElapsedHours(
    group.elapsedHours,
    group.clockStartedAt,
    nowMs
  );
  if (hours == null) return null;
  return formatClockHours(hours);
};

const CheckList = styled(Flex)`
  list-style: none;
`;

const GreyLine = ({ text }: { text: string | null }) => {
  if (text == null) return null;
  return <Small color="greyDark">{text}</Small>;
};

const PastBadge = ({ past }: { past: boolean }) => {
  if (past) return <Badge variant="danger">Past SLA</Badge>;
  return null;
};

const GroupClock = ({
  group,
  nowMs,
}: {
  group: ApprovalGroup;
  nowMs: number;
}) => (
  <>
    <GreyLine text={elapsedText(group, nowMs)} />
    <GreyLine text={slaTextOf(group.slaHours)} />
    <PastBadge past={groupIsPast(nowMs)(group)} />
  </>
);

const UndoButton = ({
  group,
  order,
  actions,
}: {
  group: ApprovalGroup;
  order: number;
  actions: ChainActions;
}) => {
  if (group.canUndo) {
    return (
      <Button
        small
        type="button"
        variant="ghost"
        disabled={actions.saving}
        onClick={() => actions.undo(order, group.approverGroupId)}
      >
        {undoLabel(group)}
      </Button>
    );
  }
  return null;
};

const GroupActions = ({
  group,
  order,
  actions,
}: {
  group: ApprovalGroup;
  order: number;
  actions: ChainActions;
}) => (
  <>
    <GuardrailDecisionForm
      group={group}
      order={order}
      actions={actions}
      layout="inline"
    />
    <UndoButton group={group} order={order} actions={actions} />
  </>
);

const AddingRulesLine = ({ group }: { group: ApprovalGroup }) => {
  const rules = group.addingRules ?? [];
  if (rules.length === 0) return null;
  return (
    <>
      {rules.map((rule) => (
        <Small key={rule.ruleId} color="greyDark">
          Added by rule: {rule.ruleName?.trim() || rule.ruleId}
        </Small>
      ))}
    </>
  );
};

const GroupHeading = ({
  group,
  nowMs,
}: {
  group: ApprovalGroup;
  nowMs: number;
}) => (
  <P fontSize="1" m="0">
    {groupStatusLine(group, nowMs)}
  </P>
);

const GroupRow = ({
  group,
  order,
  actions,
}: {
  group: ApprovalGroup;
  order: number;
  actions: ChainActions;
}) => (
  <Flex
    flexDirection="column"
    gap="xs"
    data-testid={`guardrail-approval-group-${order}-${group.approverGroupId}`}
  >
    <GroupHeading group={group} nowMs={actions.nowMs} />
    <AddingRulesLine group={group} />
    <GroupClock group={group} nowMs={actions.nowMs} />
    <GroupActions group={group} order={order} actions={actions} />
  </Flex>
);

const GroupList = ({
  groups,
  order,
  actions,
}: {
  groups: ApprovalGroup[];
  order: number;
  actions: ChainActions;
}) =>
  groups.map((group) => (
    <GroupRow
      key={group.approverGroupId}
      group={group}
      order={order}
      actions={actions}
    />
  ));

const StepChevron = ({ index }: { index: number }) => {
  if (index === 0) return null;
  return <Icon icon="chevron-right" variant="grey" aria-hidden="true" />;
};

const ReasonPendingCta = ({ actions }: { actions: ChainActions }) => (
  <Button variant="ghost" small type="button" onClick={actions.openReasonModal}>
    {EXCEPTION_REASON_CTA}
  </Button>
);

const ExceptionReasonModal = ({
  visible,
  step,
  draft,
  saving,
  error,
  onChange,
  onClose,
  onSave,
}: {
  visible: boolean;
  step: ApprovalStep;
  draft: string;
  saving: boolean;
  error: string | null;
  onChange: (value: string) => void;
  onClose: () => void;
  onSave: (reason: string) => void;
}) => {
  if (!visible) return null;
  return (
    <Modal
      visible
      onClose={onClose}
      ariaLabel={EXCEPTION_REASON_MODAL_TITLE}
      width="640px"
      maxWidth="95vw"
      maxHeight="90vh"
      headerContent={<H3>{EXCEPTION_REASON_MODAL_TITLE}</H3>}
    >
      <Box data-testid="guardrail-exception-reason-modal">
        <ExceptionReasonForm
          layout="modal"
          draft={draft}
          saving={saving}
          error={error}
          fieldId={`guardrail-exception-reason-${step.order}`}
          onChange={onChange}
          onSave={onSave}
          onCancel={onClose}
        />
      </Box>
    </Modal>
  );
};

const SavedReason = ({
  step,
  nowMs,
}: {
  step: ApprovalStep;
  nowMs: number;
}) => (
  <Flex flexDirection="column" gap="xs">
    <P fontSize="1" m="0">
      {reasonValue(step)}
    </P>
    <ExceptionReasonAttribution
      personName={step.personName}
      personId={step.personId}
      stamp={
        typeof step.actedAt === "string"
          ? formatRelativeAgo(step.actedAt, nowMs)
          : null
      }
    />
  </Flex>
);

const pendingOrSaved = (step: ApprovalStep, actions: ChainActions) => {
  if (isPending(step) && !actions.readOnly) {
    return <ReasonPendingCta actions={actions} />;
  }
  return <SavedReason step={step} nowMs={actions.nowMs} />;
};

const ReasonFields = ({
  step,
  actions,
}: {
  step: ApprovalStep;
  actions: ChainActions;
}) => {
  if (isReasonStep(step)) return pendingOrSaved(step, actions);
  return null;
};

const ApprovalStepCard = ({
  step,
  index,
  actions,
}: {
  step: ApprovalStep;
  index: number;
  actions: ChainActions;
}) => {
  const groups = stepGroupsOf(step);
  return (
    <Flex
      as="li"
      alignItems="center"
      flex="0 0 auto"
      gap="s"
      data-testid={`guardrail-approval-step-${step.order}`}
    >
      <StepChevron index={index} />
      <Box
        p="s"
        borderRadius="2"
        boxBorder="default"
        minWidth="200px"
        maxWidth={stepWidth(groups.length)}
        bg={stepBackground(stepPast(groups, actions.nowMs))}
      >
        <Flex flexDirection="column" gap="xs">
          <P fontSize="1" m="0">
            {stepHeading(step, actions.steps)}
          </P>
          <ReasonFields step={step} actions={actions} />
          <GroupList groups={groups} order={step.order} actions={actions} />
        </Flex>
      </Box>
    </Flex>
  );
};

const StepsList = ({
  steps,
  actions,
}: {
  steps: ApprovalStep[];
  actions: ChainActions;
}) => {
  if (steps.length === 0) return null;
  return (
    <CheckList
      forwardedAs="ol"
      flexDirection="row"
      flexWrap="nowrap"
      alignItems="stretch"
      gap="s"
      m="0"
      mt="s"
      p="0"
      overflow="auto"
      data-testid="guardrail-approval-steps"
      data-layout="horizontal"
    >
      {steps.map((step, index) => (
        <ApprovalStepCard
          key={step.order}
          step={step}
          index={index}
          actions={actions}
        />
      ))}
    </CheckList>
  );
};

const ErrorLine = ({ actionError }: { actionError: string | null }) => {
  if (actionError) {
    return (
      <P fontSize="1" m="0" mt="s">
        {actionError}
      </P>
    );
  }
  return null;
};

const OutcomeCard = ({
  announcementRole,
  badge,
  checks,
  steps,
  actions,
  actionError,
  historyRows,
}: {
  announcementRole: "alert" | "status";
  badge: HeaderBadge;
  checks: GuardrailCheck[];
  steps: ApprovalStep[];
  actions: ChainActions;
  actionError: string | null;
  historyRows: ReturnType<typeof guardrailHistory>;
}) => (
  <Card role={announcementRole} data-testid="requisition-guardrail-outcome">
    <GuardrailCardHeader badge={badge} mb="s" />
    <GuardrailCheckList checks={checks} />
    <StepsList steps={steps} actions={actions} />
    {historyRows.length > 0 ? (
      <Box mt="s">
        <GuardrailHistory rows={historyRows} />
      </Box>
    ) : null}
    <ErrorLine actionError={actionError} />
  </Card>
);

const RequisitionGuardrailOutcome = ({
  result,
  announcementRole = "alert",
  refusalReasons,
  readOnly = false,
}: {
  result?: GuardrailResult | null;
  announcementRole?: "alert" | "status";
  /** Story and test override. Production reads the workspace list. */
  refusalReasons?: RefusalReason[];
  /** Inbox deep link that no longer needs the caller's action — no writes. */
  readOnly?: boolean;
}) => {
  const chain = useGuardrailChainActions(result, readOnly, refusalReasons);
  if (chain.decision == null) {
    return null;
  }
  const badge = headerBadge(
    chain.decision,
    chain.shown?.guardrailEvaluation?.template
  );
  const shown = chain.shown ?? result ?? null;
  const historyRows = guardrailHistory({
    openedDate: result?.openedDate ?? shown?.openedDate ?? null,
    guardrailEvaluation: shown?.guardrailEvaluation ?? null,
    guardrailDecisionLog: shown?.guardrailDecisionLog ?? null,
    lifecycleEvents:
      chain.shown?.lifecycleEvents ?? result?.lifecycleEvents ?? null,
  });
  return (
    <>
      <OutcomeCard
        announcementRole={announcementRole}
        badge={badge}
        checks={chain.checks}
        steps={chain.steps}
        actions={chain.actions}
        actionError={chain.actionError}
        historyRows={historyRows}
      />
      {!readOnly && chain.pendingReasonStep ? (
        <ExceptionReasonModal
          visible={chain.reasonModalOpen}
          step={chain.pendingReasonStep}
          draft={chain.reasonDraft}
          saving={chain.actions.saving}
          error={chain.reasonModalError}
          onChange={chain.onReasonDraft}
          onClose={chain.closeReasonModal}
          onSave={chain.actions.saveReason}
        />
      ) : null}
    </>
  );
};

export default RequisitionGuardrailOutcome;
